import queryString from "query-string"
import slugify from "slugify"
import { navigate } from "gatsby"
import { useToast } from "@mallardbay/lib-react-components"

import { version } from "~package"
import type { LicenseInfoFieldsFragment } from "~graphql/licenses.generated"
import type { AmenityFieldsFragment } from "~graphql/amenities.generated"
import type {
    MinimalOutfitterFieldsFragment,
    OutfitterFieldsFragment,
} from "~graphql/outfitters.generated"
import type { MinimalListingFieldsFragment } from "~graphql/listings.generated"
import { OutfitterStatus } from "~graphql/generated/types"
import { MAX_STATEMENT_DESCRIPTOR_LENGTH } from "~config/constants"
import COPY_CONSTANTS from "~config/copy-constants"
import { isLocalApiEnabled, LOCAL_DEV_API_URL } from "~config/local-dev-api"

// TODO move away from impoting package
export function getVersionNumber() {
    return version
}

export function sortAmenitiesByName(
    amenityA: AmenityFieldsFragment,
    amenityB: AmenityFieldsFragment
): number {
    if (amenityA.name > amenityB.name) return 1
    if (amenityA.name < amenityB.name) return -1
    return 0
}

export function sortLicensesByState(
    licenseA: LicenseInfoFieldsFragment,
    licenseB: LicenseInfoFieldsFragment
): number {
    if (licenseA.state > licenseB.state) return 1
    if (licenseA.state < licenseB.state) return -1
    return 0
}

export function filterApprovedOutfitters(
    outfitters: MinimalOutfitterFieldsFragment[]
): MinimalOutfitterFieldsFragment[] {
    return outfitters.filter(
        (outfitter) => outfitter.status === OutfitterStatus.Approved
    )
}

export function filterPublishedListings<
    ListingType extends MinimalListingFieldsFragment,
>(listings: ListingType[]) {
    return listings.filter((listing) => listing.published)
}

export function filterListingsWithoutAvailability<
    ListingType extends MinimalListingFieldsFragment,
>(listings: ListingType[]) {
    return listings.filter((listing) => !listing.next_availability)
}

export function getUrlQueryParamsObj(): queryString.ParsedQuery {
    if (isServerSide()) {
        return {}
    }
    const { search } = window.location
    return queryString.parse(search)
}

export function navigateToSearchParams<
    TParams extends Record<string, string | number>,
>(newParams: TParams, shouldIgnorePriorParams?: boolean) {
    const queryParams = getUrlQueryParamsObj()

    const params = queryString.stringify({
        ...(shouldIgnorePriorParams ? {} : queryParams),
        ...newParams,
    })

    void navigate(`?${params}`, { replace: true })
}

/**
 * Parses a string to a positive integer (min 1). Returns defaultValue when
 * value is null, empty, or NaN. If parsed number is <= 0, returns 1.
 */
export function parsePositiveInt(
    value: string | null,
    defaultValue: number
): number {
    if (value === null || value === "") return defaultValue
    const parsed = parseInt(value, 10)
    if (Number.isNaN(parsed)) return defaultValue
    return Math.max(1, parsed)
}

export function getUrlQueryParamInt(
    paramName: string,
    defaultParamValue: string
) {
    const param = getUrlQueryParam(paramName)

    return parseInt(param || defaultParamValue, 10)
}

export function getUrlQueryParam(paramName: string) {
    const paramsObj = getUrlQueryParamsObj()

    const param = paramsObj[paramName]
    const isParamValid = typeof param === "string"

    return isParamValid ? param : null
}

export function getBooleanUrlQueryParam(paramName: string) {
    const param = getUrlQueryParam(paramName)

    return param === "true"
}

function isServerSide() {
    return typeof window === "undefined"
}

// Adds or removes an item from an array depending on if it exists (or passes custom bool check)
export const adjustArrayWithItem = <T>(
    array: T[],
    item: T,
    boolCheck?: (item: T) => boolean
): T[] => {
    if (
        boolCheck
            ? array.some((arrayItem) => boolCheck(arrayItem))
            : array.includes(item)
    ) {
        return array.filter((arrayItem) =>
            boolCheck ? !boolCheck(arrayItem) : arrayItem !== item
        )
    }

    return [...array, item]
}

// Removes duplicate items from an array, pass in a field for objects (usually id)
export const dedupeArray = <T>(
    arrayToDedupe: T[],
    fieldToCheck?: keyof T
): T[] => {
    if (fieldToCheck) {
        return arrayToDedupe.filter(
            (item, idx, array) =>
                idx ===
                array.findIndex(
                    (otherItem) =>
                        otherItem[fieldToCheck] === item[fieldToCheck]
                )
        )
    }
    return arrayToDedupe.filter(
        (item, idx, array) => idx === array.indexOf(item)
    )
}

// Sorts an array by the created_at field, with the newest first
export const sortNewestLast = <T extends { created_at: number }>(
    array: T[] = []
): T[] => [...array].sort((itemA, itemB) => itemA.created_at - itemB.created_at)

export const getUTCNoon = (date: Date): number => {
    return Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12,
        0,
        0
    )
}

export function sortByPropertyAsc<T>(array: T[], property: keyof T): T[] {
    return array.toSorted((left, right) => {
        const aValue = left[property]
        const bValue = right[property]

        if (aValue > bValue) return 1
        if (aValue < bValue) return -1

        return 0
    })
}

// Updating item at array in index
export const updateItemAtIndex = <T>(
    array: T[],
    item: T,
    index: number
): T[] => {
    return [...array.slice(0, index), item, ...array.slice(index + 1)]
}

export function getArrayOfCount(length: number): number[] {
    return Array.from({ length })
        .fill(null)
        .map((_, index) => index)
}

export function isNil(value: unknown): value is null | undefined {
    return value === null || value === undefined
}

export function groupByKey<TItem, K extends keyof TItem>(
    array: TItem[],
    key: K
): Record<string, TItem[]> {
    return array.reduce<Record<string, TItem[]>>(
        (groupedObject, currentItem) => {
            const currentKey = String(currentItem[key])

            if (!groupedObject[currentKey]) {
                groupedObject[currentKey] = []
            }

            groupedObject[currentKey].push(currentItem)

            return groupedObject
        },
        {}
    )
}

// Used to conditionally spread objects for updates / creates
// Replaces ...(value ? { key: value } : {})
export function ifDefined(
    obj: Record<string, unknown>
): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => {
            return value !== undefined
        })
    )
}

export async function getAndOpenPdfInNewTab({
    getPdfFunc,
}: {
    getPdfFunc: () => Promise<string | null>
}) {
    const pdf = await getPdfFunc()

    if (!pdf) return

    openPdfInNewTab(pdf)
}

function openPdfInNewTab(base64: string) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO fix eslint
    const buffer = Buffer.from(base64 ?? "", "base64")
    const blob = new Blob([buffer], {
        type: "application/pdf",
    })
    const objectUrl = URL.createObjectURL(blob)

    // There's another method using window.open, but we need to use
    // the anchor element trick instead to avoid safari detecting this
    // as a popup and blocking it.
    const anchorElement = document.createElement("a")
    anchorElement.href = objectUrl
    anchorElement.target = "_blank"
    anchorElement.click()
}

// matches servers implementation of slugify for outfitter names
export function slugifyOutfitterName(name: string) {
    return slugify(name, {
        replacement: "-",
        remove: /[*+~.()'"!:@]/g,
        locale: "en",
        lower: true,
        strict: true,
    })
}

export function omit<TObject extends object>(
    object?: TObject | null,
    keysToOmit: (keyof TObject)[] = []
) {
    if (!object) return undefined

    const objectCopy = { ...object }
    keysToOmit.forEach((key) => delete objectCopy[key])

    return objectCopy
}

// remove any keys that are not in the entity
// also remove any keys that have not changed from the entities values
export function getCleanedUpdateInput<
    TEntity extends Record<string, unknown>,
    TUpdateInput extends Record<string, unknown>,
>({ entity, updateInput }: { entity: TEntity; updateInput: TUpdateInput }) {
    const entityKeys = Object.keys(entity)
    const updateInputKeys = Object.keys(updateInput)

    return updateInputKeys.reduce((cleanedUpdateInput, key) => {
        if (!entityKeys.includes(key)) {
            return cleanedUpdateInput
        }

        const entityValue = entity[key]
        const updateInputValue = updateInput[key]

        if (entityValue !== updateInputValue) {
            return { ...cleanedUpdateInput, [key]: updateInputValue }
        }

        return cleanedUpdateInput
    }, {})
}

export function mapEntitiesToIdList<EntityType extends { id: string }>(
    entities: EntityType[]
) {
    return entities.map(({ id }) => id)
}

export function getDefaultPaymentDescriptor(
    outfitter?: OutfitterFieldsFragment | null
) {
    if (!outfitter?.name) {
        return undefined
    }

    return outfitter.name.slice(0, MAX_STATEMENT_DESCRIPTOR_LENGTH)
}

export function useCopyUrlToClipboard() {
    const { showSuccess, showError } = useToast()

    return async (url: string) => {
        try {
            await navigator.clipboard.writeText(url)
            showSuccess(COPY_CONSTANTS.COPIED_TO_CLIPBOARD)
        } catch (error) {
            showError(COPY_CONSTANTS.FAILED_TO_COPY_TO_CLIPBOARD)
        }
    }
}

export function parseJsonSafely(jsonString: string) {
    try {
        return JSON.parse(jsonString)
    } catch {
        return null
    }
}

/**
 * Calculates the total number of pages from a count and page size.
 * Guards against division by zero by ensuring pageSize is positive and non-zero.
 *
 * @param count - Total number of items
 * @param pageSize - Number of items per page
 * @returns Total number of pages (0 if count is 0, otherwise Math.ceil(count / safePageSize))
 */
export function calculatePages(count: number, pageSize: number): number {
    if (count === 0) {
        return 0
    }

    // Guard against division by zero: ensure pageSize is positive and non-zero
    const safePageSize = pageSize > 0 ? pageSize : 1

    return Math.ceil(count / safePageSize)
}

export function getApiUrl() {
    if (isLocalApiEnabled()) {
        return LOCAL_DEV_API_URL
    }

    return process.env.GATSBY_API_URL ?? ""
}

export function getDirectRestApiUrl() {
    if (isLocalApiEnabled()) {
        return LOCAL_DEV_API_URL
    }

    const directUrl = process.env.GATSBY_DIRECT_REST_API_URL
    return directUrl && directUrl.trim() !== "" ? directUrl : getApiUrl()
}
